// src/pages/Accounting/BankTransactions/constants.ts

import type { Bank, Country, CountryCode } from "./types";
import type { TableColumnDef } from "../../../components/common/tableCustomization";

export const PALETTE = {
  ink: "#0E1A1A",
  ink2: "#263238",
  ink3: "#3A4A52",
  ink4: "#6B7684",
  line: "#D8DEE6",
  lineSoft: "#E7EAF0",
  panel: "#F7F8FA",
  tealDark: "#0F6E56",
  amber: "#F59E0B",
  amberInk: "#92400E",
  red: "#DC2626",
  velaDeep: "#4C1D95",
  vela: "#6B46C1",
  velaLight: "#8B5CF6",
  velaWash: "#EDE9FE",
  velaFade: "#F5F3FF",
} as const;

export const COUNTRIES: Country[] = [
  { code: "ca", name: "Canada", flag: "🇨🇦" },
  { code: "us", name: "United States", flag: "🇺🇸" },
  { code: "gb", name: "United Kingdom", flag: "🇬🇧" },
  { code: "au", name: "Australia", flag: "🇦🇺" },
  { code: "in", name: "India", flag: "🇮🇳" },
  { code: "fr", name: "France", flag: "🇫🇷" },
  { code: "mx", name: "Mexico", flag: "🇲🇽" },
  { code: "br", name: "Brazil", flag: "🇧🇷" },
  { code: "other", name: "Other", flag: "🌐" },
];

export const DEFAULT_COUNTRY: CountryCode = "ca";

// Brand color per bank code, used by BankLogo.
// Add textSize when the shortName is 4+ chars so it fits the 32px square.
export const BANK_BRAND_COLORS: Record<
  string,
  { bg: string; dot?: string; textSize?: number }
> = {
  // Canada
  rbc: { bg: "#0051A5", dot: "#FEDF01" },
  td: { bg: "#008A00" },
  scotia: { bg: "#EC111A" },
  bmo: { bg: "#0079C1" },
  cibc: { bg: "#C8102E", textSize: 10 },
  nbc: { bg: "#D52B1E", textSize: 10 },
  tangerine: { bg: "#F58025" },
  desjardins: { bg: "#00874E" },
  simplii: { bg: "#0066B2", textSize: 10 },

  // United States
  chase: { bg: "#117ACA" },
  boa: { bg: "#012169", textSize: 10 },
  wells: { bg: "#D71E28", textSize: 10 },
  citi: { bg: "#003B70", textSize: 10 },
  capital: { bg: "#D22E1E", textSize: 10 },
  usbank: { bg: "#0C2340", textSize: 10 },
  pnc: { bg: "#F58025", textSize: 10 },

  // United Kingdom
  barclays: { bg: "#00AEEF" },
  "hsbc-uk": { bg: "#DB0011", textSize: 10 },
  lloyds: { bg: "#006A4D" },
  natwest: { bg: "#5A287D", textSize: 10 },
  "santander-uk": { bg: "#EC0000" },
  halifax: { bg: "#0054A4" },
  nationwide: { bg: "#002F6C" },

  // Australia
  cba: { bg: "#FFD100", textSize: 10 },
  anz: { bg: "#004C8C", textSize: 10 },
  nab: { bg: "#D20000", textSize: 10 },
  westpac: { bg: "#CD1F2E" },
  "ing-au": { bg: "#FF6200", textSize: 10 },
  macquarie: { bg: "#000000" },

  // India
  hdfc: { bg: "#004C8F", textSize: 10 },
  icici: { bg: "#AD1E23", textSize: 10 },
  sbi: { bg: "#22409A", textSize: 10 },
  axis: { bg: "#97144D" },
  kotak: { bg: "#ED1C24" },
  yes: { bg: "#0033A1" },
  indusind: { bg: "#990000", textSize: 10 },

  // France
  bnp: { bg: "#009A75", textSize: 10 },
  "credit-agricole": { bg: "#006A3A" },
  socgen: { bg: "#E60028" },
  "banque-postale": { bg: "#002E6D", textSize: 10 },
  "credit-mutuel": { bg: "#003C7D" },
  lcl: { bg: "#003A7E", textSize: 10 },

  // Mexico
  "bbva-mx": { bg: "#004481", textSize: 10 },
  banorte: { bg: "#D20019" },
  "santander-mx": { bg: "#EC0000" },
  banamex: { bg: "#013A80", textSize: 10 },
  "hsbc-mx": { bg: "#DB0011", textSize: 10 },
  "scotia-mx": { bg: "#EC111A" },

  // Brazil
  itau: { bg: "#FF6600" },
  bradesco: { bg: "#CC092F", textSize: 10 },
  bb: { bg: "#FFEF38", textSize: 10 },
  "santander-br": { bg: "#EC0000" },
  caixa: { bg: "#0066B3", textSize: 10 },
  nubank: { bg: "#820AD1" },
};

export const ALL_BANKS: Bank[] = [
  // ===== Canada =====
  { code: "rbc", name: "RBC Royal Bank", shortName: "RBC", country: "ca", isPopular: true },
  { code: "td", name: "TD Canada Trust", shortName: "TD", country: "ca", isPopular: true },
  { code: "scotia", name: "Scotiabank", shortName: "S", country: "ca", isPopular: true },
  { code: "bmo", name: "BMO", shortName: "M", country: "ca", isPopular: true },
  { code: "cibc", name: "CIBC", shortName: "CIBC", country: "ca", isPopular: true },
  { code: "nbc", name: "National Bank", shortName: "NBC", country: "ca", isPopular: true },
  { code: "tangerine", name: "Tangerine", shortName: "T", country: "ca", isPopular: true },
  { code: "desjardins", name: "Desjardins", shortName: "D", country: "ca", isPopular: true },
  { code: "simplii", name: "Simplii", shortName: "SF", country: "ca", isPopular: true },

  // ===== United States =====
  { code: "chase", name: "Chase", shortName: "Ch", country: "us", isPopular: true },
  { code: "boa", name: "Bank of America", shortName: "BoA", country: "us", isPopular: true },
  { code: "wells", name: "Wells Fargo", shortName: "WF", country: "us", isPopular: true },
  { code: "citi", name: "Citi", shortName: "citi", country: "us", isPopular: true },
  { code: "capital", name: "Capital One", shortName: "C1", country: "us", isPopular: true },
  { code: "usbank", name: "US Bank", shortName: "USB", country: "us", isPopular: true },
  { code: "pnc", name: "PNC Bank", shortName: "PNC", country: "us", isPopular: true },

  // ===== United Kingdom =====
  { code: "barclays", name: "Barclays", shortName: "B", country: "gb", isPopular: true },
  { code: "hsbc-uk", name: "HSBC UK", shortName: "HSBC", country: "gb", isPopular: true },
  { code: "lloyds", name: "Lloyds Bank", shortName: "L", country: "gb", isPopular: true },
  { code: "natwest", name: "NatWest", shortName: "NW", country: "gb", isPopular: true },
  { code: "santander-uk", name: "Santander UK", shortName: "S", country: "gb", isPopular: true },
  { code: "halifax", name: "Halifax", shortName: "H", country: "gb", isPopular: true },
  { code: "nationwide", name: "Nationwide", shortName: "N", country: "gb", isPopular: true },

  // ===== Australia =====
  { code: "cba", name: "Commonwealth Bank", shortName: "CBA", country: "au", isPopular: true },
  { code: "anz", name: "ANZ", shortName: "ANZ", country: "au", isPopular: true },
  { code: "nab", name: "National Australia Bank", shortName: "NAB", country: "au", isPopular: true },
  { code: "westpac", name: "Westpac", shortName: "W", country: "au", isPopular: true },
  { code: "ing-au", name: "ING Australia", shortName: "ING", country: "au", isPopular: true },
  { code: "macquarie", name: "Macquarie", shortName: "M", country: "au", isPopular: true },

  // ===== India =====
  { code: "hdfc", name: "HDFC Bank", shortName: "HDFC", country: "in", isPopular: true },
  { code: "icici", name: "ICICI Bank", shortName: "ICICI", country: "in", isPopular: true },
  { code: "sbi", name: "State Bank of India", shortName: "SBI", country: "in", isPopular: true },
  { code: "axis", name: "Axis Bank", shortName: "A", country: "in", isPopular: true },
  { code: "kotak", name: "Kotak Mahindra", shortName: "K", country: "in", isPopular: true },
  { code: "yes", name: "Yes Bank", shortName: "Y", country: "in", isPopular: true },
  { code: "indusind", name: "IndusInd", shortName: "IND", country: "in", isPopular: true },

  // ===== France =====
  { code: "bnp", name: "BNP Paribas", shortName: "BNP", country: "fr", isPopular: true },
  { code: "credit-agricole", name: "Crédit Agricole", shortName: "CA", country: "fr", isPopular: true },
  { code: "socgen", name: "Société Générale", shortName: "SG", country: "fr", isPopular: true },
  { code: "banque-postale", name: "La Banque Postale", shortName: "LBP", country: "fr", isPopular: true },
  { code: "credit-mutuel", name: "Crédit Mutuel", shortName: "CM", country: "fr", isPopular: true },
  { code: "lcl", name: "LCL", shortName: "LCL", country: "fr", isPopular: true },

  // ===== Mexico =====
  { code: "bbva-mx", name: "BBVA Mexico", shortName: "BBVA", country: "mx", isPopular: true },
  { code: "banorte", name: "Banorte", shortName: "BAN", country: "mx", isPopular: true },
  { code: "santander-mx", name: "Santander Mexico", shortName: "S", country: "mx", isPopular: true },
  { code: "banamex", name: "Banamex", shortName: "BNX", country: "mx", isPopular: true },
  { code: "hsbc-mx", name: "HSBC Mexico", shortName: "HSBC", country: "mx", isPopular: true },
  { code: "scotia-mx", name: "Scotiabank Mexico", shortName: "S", country: "mx", isPopular: true },

  // ===== Brazil =====
  { code: "itau", name: "Itaú", shortName: "I", country: "br", isPopular: true },
  { code: "bradesco", name: "Bradesco", shortName: "BRA", country: "br", isPopular: true },
  { code: "bb", name: "Banco do Brasil", shortName: "BB", country: "br", isPopular: true },
  { code: "santander-br", name: "Santander Brasil", shortName: "S", country: "br", isPopular: true },
  { code: "caixa", name: "Caixa", shortName: "CX", country: "br", isPopular: true },
  { code: "nubank", name: "Nubank", shortName: "N", country: "br", isPopular: true },
];

export const METHOD_LABELS: Record<string, string> = {
  "e-transfer": "e-Transfer",
  "direct-deposit": "Direct deposit",
  "debit-card": "Debit card",
  "credit-card": "Credit card",
  "pre-auth-debit": "Pre-auth debit",
  wire: "Wire",
  transfer: "Transfer",
  cheque: "Cheque",
};

export const STATUS_LABELS: Record<string, string> = {
  auto: "Auto",
  review: "Review",
  reconciled: "Reconciled",
};

// Column defs for the Bank transactions table. Fed to useTableCustomization + TableCustomizeDrawer.
// Payee is always on; the rest are togglable.
export const BANK_TX_TABLE_COLUMNS: TableColumnDef[] = [
  { key: "date", label: "Date", togglable: true, defaultVisible: true },
  { key: "payee", label: "Payee", togglable: false, defaultVisible: true },
  { key: "category", label: "Category", togglable: true, defaultVisible: true },
  { key: "amount", label: "Amount", togglable: true, defaultVisible: true },
];